import React from "react";
import { Link } from "react-router-dom";
import { Printer, FileText, Sparkles, ExternalLink } from "lucide-react";

/**
 * Admin → Print Pack panel.
 *
 * Quick access to the printable assets that promote the app off-screen
 * (poster for receptions / outdoor shows; business cards for handouts).
 *
 * Each item links to the route + a short "what's it for" caption so a
 * non-technical operator (Anthony) can find them again in 6 months.
 */
const ITEMS = [
  {
    title: "Launch poster · A4",
    subtitle: "Hero photo · QR code · 3-step install · Campfire Code",
    blurb:
      "Print on A4 (or scale to A3) for campsite receptions, outdoor shows, community noticeboards. Two taps and the visitor is on the home screen.",
    href: "/print/poster",
    testid: "print-pack-poster",
  },
  {
    title: "Business cards · 10 per A4",
    subtitle: "85×55mm UK standard · QR on every card",
    blurb:
      "Hand out at festivals, leave in shop windows, slip into trip-camper goodie bags. Print on 250gsm card stock and cut along the dashed lines (any high-street print shop).",
    href: "/print/cards",
    testid: "print-pack-cards",
  },
  {
    title: "Reel card · Instagram & TikTok",
    subtitle: "9:16 vertical · QR · launch date · FB + IG handles",
    blurb:
      "A ready-to-screenshot or screen-record vertical card for Reels and Stories. Massive QR, the live campsite count, the launch date (31 May 2026), and your social handles — built to be readable from a thumbnail.",
    href: "/share/reel-card",
    testid: "print-pack-reel",
  },
  {
    title: "Reels kit · 6 themed scenes",
    subtitle: "9:16 vertical · screen-record each scene for a Reel",
    blurb:
      "Six pre-built 9:16 scenes (Hook · Honest mission · Live counter · Use case · Founder note · Launch CTA). Open the page on your phone, screen-record while you scroll between scenes, and you've got a 15-30 s Reel — no editing software needed.",
    href: "/share/reels-kit",
    testid: "print-pack-reels-kit",
  },
  {
    title: "Variety reels · 7-day series 🌈",
    subtitle: "9:16 vertical · subtle signup · one camping style per day",
    blurb:
      "Bright, colour-coded carousel showcasing every way you can sleep outside — tent · pod · van · caravan · chalet · hostel · bothy. Short, snappy hooks with the signup nudge held back until the final summary card, so each post feels like inspiration rather than a sales pitch. Includes a ready-to-paste 7-day caption + sound pack.",
    href: "/share/reels-variety",
    testid: "print-pack-reels-variety",
  },
  // iter121 — Fishing × Camping launch reel
  {
    title: "🎣 Camp × Fish · launch reel · NEW",
    subtitle: "9:16 vertical · 3 scenes · fisheries-near-every-pitch feature launch",
    blurb:
      '"Love camping? Love fishing? Why not combine the two?" Scene 1 = the hook · Scene 2 = live pairing (Orcaber Farm ←→ nearest named fishery, distance auto-calculated) · Scene 3 = "Find your pair on camp-compass.uk". Bright cyan/yellow/orange palette so it pops in feeds. Two captions pre-written for socials.',
    href: "/share/fishing-camping",
    testid: "print-pack-fishing-camping",
  },
  // iter121 — Poll-vote post (one-tap engagement post for socials)
  {
    title: "🗳 Poll-vote post · one-tap engagement",
    subtitle: "9:16 vertical · live poll snippet · routes straight to vote",
    blurb:
      "A single shareable scene that mirrors today's homepage poll (the question + top 4 leading options + live vote count). One tap from social and the visitor lands on the Landing-page poll where they vote in one tap — perfect for warming up a cold audience.",
    href: "/share/poll-vote",
    testid: "print-pack-poll-vote",
  },
  // iter121 — 5 promo reels for the most-clicked campsites
  // iter123 — Share-your-trip reel (Campfire Chat community pull)
  {
    title: "📣 Share Your Trip · Campfire community reel · NEW",
    subtitle: "9:16 vertical · 4 scenes · pushes existing users into community threads",
    blurb:
      'Bright Orcaber Farm photo (real, not stock) over a sunny amber palette. Scene 1 = the invite. Scene 2 = four thread CTAs (Trip Reports · Pets · Families · Positivity). Scene 3 = "the kindest UK camping community" (three social proof reasons). Scene 4 = QR + Instagram + Facebook + 1-tap into /community. Page also includes direct deep-links per thread tag so the social bio can carry "Open Pets thread →" etc. Long + short captions ready to copy.',
    href: "/share/share-your-trip",
    testid: "print-pack-share-your-trip",
  },
  {
    title: "🔥 Trending site reel · Orcaber Farm Camping",
    subtitle: "9:16 · Yorkshire Dales · 3 scenes (hero · facilities · CTA)",
    blurb:
      "Reusable universal reel pulled from the live DB — cover photo, region, rating, description, pitch types and a one-tap listing link, all auto-refreshing as we update the Mongo record. Footer ribbon burns camp-compass.uk into every scene.",
    href: "/share/promo/cs_osm_1031838811",
    testid: "print-pack-promo-orcaber",
  },
  {
    title: "🔥 Trending site reel · Great Langdale (Lake District)",
    subtitle: "9:16 · Lake District, Cumbria · 4.7★",
    blurb:
      "National Trust Lake District favourite. Same 3-scene template, live DB content. Drop into Reels/TikTok/Facebook as a carousel post.",
    href: "/share/promo/cs_724b96e72a",
    testid: "print-pack-promo-langdale",
  },
  {
    title: "🔥 Trending site reel · Trevalgan Touring Park (Cornwall)",
    subtitle: "9:16 · St Ives, Cornwall · 4.8★",
    blurb:
      "Cornwall coast favourite. Live DB-pulled hero photo, facilities and rating. Caption pre-written for Instagram + Stories.",
    href: "/share/promo/cs_1fc06bfcd4",
    testid: "print-pack-promo-trevalgan",
  },
  {
    title: "🔥 Trending site reel · Morvich (Scottish Highlands)",
    subtitle: "9:16 · Kintail, Scottish Highlands · 4.7★",
    blurb:
      "Highlands trailhead favourite right next to the Five Sisters of Kintail. Auto-fetches live data; screen-record the 3 scenes for a single-post carousel.",
    href: "/share/promo/cs_746155beb3",
    testid: "print-pack-promo-morvich",
  },
  {
    title: "🔥 Trending site reel · Caerfai Bay (Pembrokeshire)",
    subtitle: "9:16 · St Davids, Pembrokeshire · 4.7★",
    blurb:
      "Cliff-top coastal favourite near St Davids. Same template, live content, baked-in URL ribbon. Pair it with the Pembrokeshire entry in the new poll for double exposure.",
    href: "/share/promo/cs_3ad53bfba9",
    testid: "print-pack-promo-caerfai",
  },
  {
    title: "🚀 Launch Day reel · WE ARE READY, ARE YOU? 🔥",
    subtitle: "9:16 vertical · 5 scenes · 14-day Pro trial CTA + QR + follow buttons",
    blurb:
      "THE launch-day reel — five bold 9:16 scenes built for screen-recording. Scene 1: WE ARE READY. ARE YOU? · Scene 2: the live campsite count + 14-day Pro trial · Scene 3: 8 feature pills · Scene 4: signup QR · Scene 5: tag-your-mates + follow CTAs. Four ready-to-paste captions underneath (signature post, short hook, bank-holiday angle, personal WhatsApp). Post this TODAY.",
    href: "/share/launch-day",
    testid: "print-pack-launch-day",
  },
  {
    title: "💎 Free vs Pro reel · show the difference",
    subtitle: "9:16 vertical · 3 scenes · side-by-side comparison + 14-day Pro trial QR",
    blurb:
      "Scene 1: big side-by-side table — 6 free features vs 10 Pro features. Scene 2: 'Why Pro makes ALL the difference' with 3 hero benefits (Camper AI, storm alerts, partner deals). Scene 3: 'TRY PRO FREE' + signup QR. Three captions: Free-vs-Pro signature post, 'why bother with Pro' TikTok angle, short Reels hook. Use this when you want to convert browsers into trial users.",
    href: "/share/free-vs-pro",
    testid: "print-pack-free-vs-pro",
  },
  {
    title: "🎁 14-day FREE Pro trial reel · zero risk",
    subtitle: "9:16 vertical · 4 scenes · trial-focused conversion + zero-risk trust panel",
    blurb:
      "Designed entirely around the 14-day free trial. Scene 1: massive '14 DAYS of PRO' hero. Scene 2: what you actually unlock (6 features). Scene 3: ZERO-RISK PROMISE panel (reminder email · one-tap cancel · £1.99/mo if you stay). Scene 4: signup QR. Three captions including a trust-builder for cautious users + a short story hook.",
    href: "/share/try-pro-free",
    testid: "print-pack-try-pro-free",
  },
  {
    title: "🔥 Campfire community reel · leave a comment",
    subtitle: "9:16 vertical · 3 scenes · community engagement + thread previews",
    blurb:
      "Convert lurkers into commenters. Scene 1: 'YOUR CAMPING PEOPLE — now open' big hero. Scene 2: 5 real thread previews with reply counts (the visible momentum). Scene 3: 'BROWSE. COMMENT. CONNECT.' + community QR. Three captions: 'Come find your camping people' signature post, TikTok lurker-converter, personal invite for Stories. Use when you want signups + early UGC.",
    href: "/share/join-the-chat",
    testid: "print-pack-join-the-chat",
  },
  {
    title: "Launch Push reels · final-week pack 🔥",
    subtitle: "9:16 vertical · live countdown · SIGN UP NOW on every scene",
    blurb:
      "Six bright, sunny, conversion-first reels built for the final week before launch. Every scene shows a live 'X days left' countdown, every QR code lands users straight on the signup form. Daily posting schedule baked in — post one a day from today through launch morning.",
    href: "/share/launch-push",
    testid: "print-pack-launch-push",
  },
  {
    title: "Final Call reel · one bold last push 📣",
    subtitle: "9:16 vertical · ONE scene · sign-up QR + Facebook + Instagram follow buttons",
    blurb:
      "A single bright, scroll-stopping reel for the absolute final push before launch. Big bold 'LAST CALL' headline, live days-left countdown, QR straight to the signup form, AND clean follow buttons for Facebook + Instagram so viewers who aren't ready to sign up still become followers. Three snappy caption variants ready to copy-paste.",
    href: "/share/final-call",
    testid: "print-pack-final-call",
  },
  {
    title: "Launch pack · social toolkit",
    subtitle: "Messenger invite · FB & IG posts · QR codes · mock threads",
    blurb:
      "Copy-paste-ready posts for Facebook & Instagram, a warm Messenger invite for friends & family, QR codes for both pages, and mock community threads you can screenshot. One-click copy on every block.",
    href: "/launch-pack",
    testid: "print-pack-launch",
  },
];

export default function PrintPackCard() {
  return (
    <section
      className="border-2 hairline-strong bg-white p-6 mb-10"
      data-testid="admin-print-pack"
    >
      <header className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Marketing toolkit
          </div>
          <h2 className="font-display text-2xl mt-1 flex items-center gap-2">
            <Printer className="w-5 h-5" strokeWidth={1.7} /> Print pack
          </h2>
          <p className="text-sm text-[#555] mt-1.5 max-w-2xl leading-relaxed">
            Branded, ready-to-print assets to promote CampCompass off-screen — no
            designer needed. Each one carries the QR code so people land on the app
            in two taps.
          </p>
        </div>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        {ITEMS.map((it) => (
          <Link
            key={it.href}
            to={it.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={it.testid}
            className="group border hairline p-5 hover:border-[#ff4500] hover:shadow-[0_4px_0_0_#ff4500] hover:-translate-y-[2px] transition-all bg-white"
          >
            <div className="flex items-start justify-between gap-3">
              <FileText className="w-5 h-5 text-[#ff4500] mt-0.5" strokeWidth={1.7} />
              <ExternalLink className="w-3.5 h-3.5 text-[#999] group-hover:text-[#ff4500] transition-colors" />
            </div>
            <h3 className="font-display text-lg leading-tight mt-3">{it.title}</h3>
            <div className="mono text-[10px] uppercase tracking-[0.14em] text-[#888] mt-1">{it.subtitle}</div>
            <p className="text-[12.5px] text-[#444] leading-relaxed mt-3">{it.blurb}</p>
            <div className="mono text-[10px] text-[#ff4500] mt-3 inline-flex items-center gap-1">
              Open & print →
            </div>
          </Link>
        ))}
      </div>

      <p className="mono text-[10px] text-[#888] mt-5 leading-snug border-t hairline pt-4">
        Tip: open in a desktop browser. Press <strong>Ctrl+P</strong> (or <strong>⌘+P</strong>),
        choose <strong>Save as PDF</strong>, set margins to <strong>None</strong> (poster) or{" "}
        <strong>Default</strong> (cards), scale <strong>100%</strong>. Email the PDF to your
        local print shop or upload to <em>Solopress / Vistaprint</em>.
      </p>
    </section>
  );
}
